// Module-level cache of global schema-viewer overrides.
//
// Previously these overrides lived in each user's browser localStorage,
// which meant two users exporting the same scope saw different "Information"
// column copy. They now live in the D1 `schema_overrides` table via the
// /api/schema-overrides worker routes.
//
// - On app boot, `hydrateSchemaOverrides()` fetches the full map once and
//   populates this cache.
// - `readSchemaOverridesSync()` is the synchronous read used by exporters
//   and the schema accessor helpers — it never touches localStorage.
// - On first load, if the server is empty and the caller has legacy
//   localStorage entries, we auto-upload them (one-time migration) so no
//   existing customisations are lost.
// - Writes go through `setSchemaOverridesEntry()`, which optimistically
//   updates the cache and persists via the API.

import { schemaOverridesApi } from '../../../api/client'
import type { SchemaOverridesMap } from '../../../api/client'

const LEGACY_LOCAL_STORAGE_KEY = 'schemaViewer:overridesByKey'

type Listener = (map: SchemaOverridesMap) => void

let cache: SchemaOverridesMap = {}
let hydrated = false
let hydratePromise: Promise<void> | null = null
const listeners = new Set<Listener>()

function notify() {
  for (const l of listeners) {
    try { l(cache) } catch { /* noop */ }
  }
}

export function subscribeToSchemaOverrides(fn: Listener): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

export function readSchemaOverridesSync(): SchemaOverridesMap {
  return cache
}

export function isSchemaOverridesHydrated(): boolean {
  return hydrated
}

function readLegacyLocalStorage(): SchemaOverridesMap {
  try {
    if (typeof window === 'undefined') return {}
    const raw = window.localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    return parsed as SchemaOverridesMap
  } catch {
    return {}
  }
}

async function migrateLegacyLocalStorage(legacy: SchemaOverridesMap): Promise<void> {
  const entries = Object.entries(legacy)
  if (entries.length === 0) return

  let migrated = 0
  for (const [entityKey, overrides] of entries) {
    if (!overrides || typeof overrides !== 'object') continue
    const cleaned: Record<string, string> = {}
    for (const [k, v] of Object.entries(overrides)) {
      if (typeof v === 'string' && v.trim().length > 0) cleaned[k] = v
    }
    if (Object.keys(cleaned).length === 0) continue
    const res = await schemaOverridesApi.save(entityKey, cleaned)
    if (res.success) {
      cache[entityKey] = cleaned
      migrated++
    }
  }

  if (migrated > 0) {
    console.info(`Migrated ${migrated} legacy schema overrides from localStorage to server.`)
    notify()
    // Keep the localStorage copy as a read-only archive in case we need to
    // recover — it's no longer read anywhere in the app.
    try {
      window.localStorage.setItem(`${LEGACY_LOCAL_STORAGE_KEY}:archived-at`, new Date().toISOString())
    } catch { /* noop */ }
  }
}

export async function hydrateSchemaOverrides(): Promise<void> {
  if (hydratePromise) return hydratePromise
  hydratePromise = (async () => {
    const res = await schemaOverridesApi.getAll()
    if (res.success && res.data) {
      cache = res.data.overridesByKey ?? {}
    } else {
      console.warn('Failed to load schema overrides from server:', res.error)
      cache = {}
    }

    // One-time migration: if the server returned nothing but the user has
    // legacy localStorage entries, push them up. Only the single admin editor
    // can actually persist them; for everyone else the POSTs will 403 and
    // the cache stays empty (which is fine — they'll see server values once
    // the admin migrates or edits).
    //
    // Once migration has been attempted (success or not), remove the legacy
    // key so the 403 noise doesn't repeat on every page load.
    const legacy = readLegacyLocalStorage()
    const serverEmpty = Object.keys(cache).length === 0
    const legacyHasData = Object.keys(legacy).length > 0
    if (serverEmpty && legacyHasData) {
      await migrateLegacyLocalStorage(legacy).catch(() => {
        // Silently ignore — most likely a 403 for non-admin users.
      })
      // Whether migration succeeded or failed, stop retrying on future loads.
      try { window.localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY) } catch { /* noop */ }
    }

    hydrated = true
    notify()
  })()
  return hydratePromise
}

export async function setSchemaOverridesEntry(
  entityKey: string,
  overrides: Record<string, string>,
): Promise<boolean> {
  // Optimistic cache update.
  const prev = cache[entityKey]
  const cleaned: Record<string, string> = {}
  for (const [k, v] of Object.entries(overrides)) {
    if (typeof v === 'string' && v.trim().length > 0) cleaned[k] = v
  }
  if (Object.keys(cleaned).length === 0) {
    delete cache[entityKey]
  } else {
    cache[entityKey] = cleaned
  }
  notify()

  const res = await schemaOverridesApi.save(entityKey, cleaned)
  if (!res.success) {
    // Roll back on failure.
    if (prev) cache[entityKey] = prev
    else delete cache[entityKey]
    notify()
    console.error('Failed to save schema override:', res.error)
    return false
  }
  return true
}

export async function clearAllSchemaOverrides(): Promise<void> {
  const keys = Object.keys(cache)
  for (const k of keys) {
    await schemaOverridesApi.remove(k).catch(() => {})
  }
  cache = {}
  notify()
}
